import {
  Timestamp,
  collection,
  deleteField,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { deleteObject, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../firebase";
import {
  createEventFlyerStoragePath,
  submitEventWithOptionalFlyer,
  validateEventFlyer,
} from "../../eventMedia";
import {
  HOUSING_DEVELOPMENT_EVENT_SERIES,
  HOUSING_DEVELOPMENT_PROGRAMME,
  SURVEY_QUESTION_TYPES,
  buildHousingSurveyAbsoluteUrl,
  buildHousingSurveyPath,
} from "../config";

const SURVEY_STATUSES = new Set(["draft", "published", "closed"]);
const EVENT_STATUSES = new Set(["draft", "approved", "cancelled"]);
const SURVEY_SLUG_RESERVATION_PREFIX = "__housing-survey-slug--";
const OPTION_TYPES = new Set([
  "yes-no",
  "single-choice",
  "multiple-choice",
  "dropdown",
  "demographic",
]);

function clean(value) {
  return String(value ?? "").normalize("NFKC").trim();
}

function slugify(value) {
  return clean(value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

function canonicalQuestionType(value) {
  const key = clean(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
  const aliases = {
    shorttext: "short-text",
    longtext: "long-text",
    textarea: "long-text",
    yesno: "yes-no",
    singlechoice: "single-choice",
    multiplechoice: "multiple-choice",
    multiselect: "multiple-choice",
    ratingscale: "rating-scale",
    rating15: "rating-1-5",
    rating1to5: "rating-1-5",
    rating110: "rating-1-10",
    rating1to10: "rating-1-10",
    scale15: "rating-1-5",
    scale110: "rating-1-10",
  };
  return aliases[key] || clean(value).toLowerCase().replace(/[\s_/]+/g, "-");
}

function stringList(value) {
  const items = Array.isArray(value) ? value : clean(value).split(/[\n,]/);
  return [...new Set(items.map((item) => clean(typeof item === "object" ? item.name || item.label : item)).filter(Boolean))];
}

function configuredQuestionTypes() {
  const values = Array.isArray(SURVEY_QUESTION_TYPES)
    ? SURVEY_QUESTION_TYPES
    : Object.values(SURVEY_QUESTION_TYPES || {});
  return new Set(values.map((item) => canonicalQuestionType(
    typeof item === "object" ? item.value || item.id || item.type : item,
  )));
}

function requireAdminUid() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Administrator authentication is required.");
  return uid;
}

function dateValue(value) {
  const input = clean(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input)) return "";
  const parsed = new Date(`${input}T12:00:00.000Z`);
  return parsed.toISOString().slice(0, 10) === input ? input : "";
}

function timeValue(value) {
  const input = clean(value);
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(input) ? input : "";
}

function safeHttpUrl(value) {
  const input = clean(value);
  if (!input) return "";
  try {
    const parsed = new URL(input);
    const loopback = ["localhost", "127.0.0.1", "[::1]"].includes(parsed.hostname);
    return parsed.protocol === "https:" || (parsed.protocol === "http:" && loopback)
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
}

function optionalNonNegativeInteger(value) {
  if (value === null || value === undefined || clean(value) === "") return null;
  const number = Number(value);
  return Number.isInteger(number) && number >= 0 ? number : null;
}

function londonEventStartDate(date, time) {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  const targetUtc = Date.UTC(year, month - 1, day, hour, minute);
  let candidate = targetUtc;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(candidate));
    const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const renderedUtc = Date.UTC(
      Number(byType.year),
      Number(byType.month) - 1,
      Number(byType.day),
      Number(byType.hour),
      Number(byType.minute),
    );
    candidate -= renderedUtc - targetUtc;
  }
  return new Date(candidate);
}

function londonDateKey(value = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

export function normaliseHousingQuestion(question = {}, index = 0) {
  const type = canonicalQuestionType(question.type) || "short-text";
  const label = clean(question.label || question.question || question.title);
  const suppliedId = clean(question.id || question.questionId || question.key);
  const generatedId = (slugify(label) || `question-${index + 1}`).slice(0, 80);
  const id = suppliedId || (/^[A-Za-z]/.test(generatedId) ? generatedId : `question-${generatedId}`);
  const options = stringList(question.options || question.choices || []);
  const helpText = clean(question.helpText || question.helper || question.help);
  const maxLength = Number(question.maxLength);
  const ratingMin = Number(question.ratingMin ?? question.min);
  const ratingMax = Number(question.ratingMax ?? question.max);
  return {
    id,
    label,
    type,
    required: question.required === true,
    helpText,
    ...(question.helper !== undefined ? { helper: helpText } : {}),
    options: type === "yes-no" && !options.length ? ["Yes", "No"] : options,
    personalData: ["email", "phone"].includes(type)
      || question.personalData === true
      || question.isPersonalData === true,
    ...(Number.isInteger(maxLength) && maxLength > 0 ? { maxLength } : {}),
    ...(Number.isFinite(ratingMin) ? { min: ratingMin, ratingMin } : {}),
    ...(Number.isFinite(ratingMax) ? { max: ratingMax, ratingMax } : {}),
    ...(clean(question.minLabel) ? { minLabel: clean(question.minLabel) } : {}),
    ...(clean(question.maxLabel) ? { maxLabel: clean(question.maxLabel) } : {}),
    ...(clean(question.autoComplete) ? { autoComplete: clean(question.autoComplete) } : {}),
  };
}

export function validateHousingSurvey(values = {}) {
  const errors = {};
  const name = clean(values.surveyName || values.title);
  const slug = slugify(values.surveySlug || values.slug || name);
  const questions = (Array.isArray(values.questions) ? values.questions : [])
    .map(normaliseHousingQuestion);
  const allowedTypes = configuredQuestionTypes();
  if (!name || name.length > 180) errors.surveyName = "Enter a survey name of up to 180 characters.";
  if (!slug) errors.surveySlug = "Enter a valid public URL slug.";
  if (!clean(values.description) || clean(values.description).length > 3000) {
    errors.description = "Enter a survey description of up to 3,000 characters.";
  }
  if (!questions.length) errors.questions = "Add at least one survey question.";
  if (questions.length > 100) errors.questions = "A survey can contain no more than 100 questions.";
  const ids = new Set();
  questions.forEach((question, index) => {
    if (!question.label) errors[`question-${index}-label`] = "Enter the question wording.";
    if (!/^[A-Za-z][A-Za-z0-9_-]{0,79}$/.test(question.id)) {
      errors[`question-${index}-id`] = "Use 1–80 characters, starting with a letter and using only letters, numbers, underscores or hyphens.";
    }
    if (ids.has(question.id)) errors[`question-${index}-id`] = "Each question needs a unique identifier.";
    ids.add(question.id);
    if (allowedTypes.size && !allowedTypes.has(question.type)) {
      errors[`question-${index}-type`] = "Choose a supported question type.";
    }
    if (OPTION_TYPES.has(question.type) && question.type !== "yes-no" && question.options.length < 2) {
      errors[`question-${index}-options`] = "Add at least two answer options.";
    }
    if (question.options.length > 100) {
      errors[`question-${index}-options`] = "Add no more than 100 answer options.";
    }
    if (question.type === "rating-scale") {
      if (!Number.isInteger(question.ratingMin) || question.ratingMin < 1 || question.ratingMin > 10) {
        errors[`question-${index}-ratingMin`] = "Choose a whole-number minimum from 1 to 10.";
      }
      if (!Number.isInteger(question.ratingMax) || question.ratingMax < 1 || question.ratingMax > 10) {
        errors[`question-${index}-ratingMax`] = "Choose a whole-number maximum from 1 to 10.";
      } else if (Number.isInteger(question.ratingMin) && question.ratingMax <= question.ratingMin) {
        errors[`question-${index}-ratingMax`] = "The maximum must be greater than the minimum.";
      }
    }
  });
  const openDate = dateValue(values.openDate);
  const closeDate = dateValue(values.closeDate);
  if (values.openDate && !openDate) errors.openDate = "Enter a valid opening date.";
  if (values.closeDate && !closeDate) errors.closeDate = "Enter a valid closing date.";
  if (openDate && closeDate && closeDate < openDate) errors.closeDate = "The closing date must be after the opening date.";
  return errors;
}

export function isHousingSurveySlugLocked(survey = {}) {
  const responseCount = Number(survey.responseCount ?? survey.responsesCount ?? 0);
  return clean(survey.status).toLowerCase() === "published"
    || survey.published === true
    || Boolean(survey.publishedAt)
    || survey.hasResponses === true
    || (Number.isFinite(responseCount) && responseCount > 0);
}

function assertSurveySlugCanChange(existing = {}, requestedSlug = "") {
  const currentSlug = slugify(existing.surveySlug || existing.slug);
  if (!currentSlug || currentSlug === requestedSlug || !isHousingSurveySlugLocked(existing)) return;
  const error = new Error("The public URL cannot be changed after a survey is published or receives responses.");
  error.code = "HOUSING_SURVEY_SLUG_LOCKED";
  error.validationErrors = {
    surveySlug: "The public URL is locked because this survey has been published or has responses.",
  };
  throw error;
}

function surveySlugConflictError() {
  const error = new Error("That survey public URL is already in use. Choose a different slug.");
  error.code = "HOUSING_SURVEY_SLUG_CONFLICT";
  error.validationErrors = {
    surveySlug: "That public URL is already used by another survey.",
  };
  return error;
}

function surveySlugReservationReference(slug) {
  return doc(db, "surveys", `${SURVEY_SLUG_RESERVATION_PREFIX}${slug}`);
}

function isSurveySlugReservation(data = {}) {
  return data.recordType === "housing-survey-slug-reservation";
}

async function assertHousingSurveySlugUnique(surveySlug, surveyId) {
  const snapshot = await getDocs(collection(db, "surveys"));
  const conflict = snapshot.docs.some((item) => {
    const data = item.data();
    const storedSlug = slugify(data.surveySlug || data.slug);
    if (storedSlug !== surveySlug) return false;
    if (isSurveySlugReservation(data)) return clean(data.surveyId) !== surveyId;
    return item.id !== surveyId;
  });
  if (conflict) throw surveySlugConflictError();
}

function assertSurveySlugReservationAvailable(snapshot, surveyId) {
  if (!snapshot.exists()) return;
  const ownerId = clean(snapshot.data().surveyId);
  if (ownerId && ownerId !== surveyId) throw surveySlugConflictError();
}

function surveySlugReservationRecord(surveySlug, surveyId, uid, timestamp, created = false) {
  return {
    recordType: "housing-survey-slug-reservation",
    surveySlug,
    slug: surveySlug,
    surveyId,
    active: false,
    published: false,
    status: "reserved",
    updatedAt: timestamp,
    updatedBy: uid,
    ...(created ? { createdAt: timestamp, createdBy: uid } : {}),
  };
}

export function buildHousingSurveyRecord(values = {}, existing = {}) {
  const errors = validateHousingSurvey(values);
  if (Object.keys(errors).length) {
    const error = new Error("HOUSING_SURVEY_INVALID");
    error.validationErrors = errors;
    throw error;
  }
  const surveyName = clean(values.surveyName || values.title);
  const surveySlug = slugify(values.surveySlug || values.slug || surveyName);
  assertSurveySlugCanChange(existing, surveySlug);
  const status = SURVEY_STATUSES.has(clean(values.status)) ? clean(values.status) : "draft";
  const eventIds = stringList(values.eventIds?.length ? values.eventIds : values.eventId);
  const partners = stringList(values.partnerOrganisations || values.partners);
  const questions = values.questions.map(normaliseHousingQuestion);
  const schemaVersion = Math.max(1, Number(
    existing.schemaVersion
      || existing.surveyVersion
      || existing.version
      || values.schemaVersion
      || values.surveyVersion
      || values.version
      || 1,
  ));
  return {
    surveyId: clean(values.surveyId || existing.surveyId || existing.id || surveySlug),
    surveyName,
    title: surveyName,
    surveySlug,
    slug: surveySlug,
    description: clean(values.description),
    eventId: eventIds[0] || "",
    eventIds,
    programme: HOUSING_DEVELOPMENT_PROGRAMME,
    partnerOrganisations: partners,
    status,
    published: status === "published",
    active: status === "published",
    publicVisibility: values.publicVisibility !== false,
    openDate: dateValue(values.openDate) || null,
    closeDate: dateValue(values.closeDate) || null,
    questions,
    schemaVersion,
    surveyVersion: schemaVersion,
    version: schemaVersion,
    publicPath: buildHousingSurveyPath(surveySlug),
    publicUrl: buildHousingSurveyAbsoluteUrl(surveySlug),
  };
}

export function validateHousingEvent(values = {}) {
  const errors = {};
  const title = clean(values.title || values.eventName);
  const date = dateValue(values.date || values.startDate);
  const startTime = timeValue(values.startTime);
  const endTime = timeValue(values.endTime);
  if (!title || title.length > 180) errors.title = "Enter an event title of up to 180 characters.";
  if (!date) errors.date = "Enter a valid event date.";
  if (!startTime) errors.startTime = "Enter a valid start time.";
  if (!endTime) errors.endTime = "Enter a valid end time.";
  if (startTime && endTime && endTime <= startTime) errors.endTime = "The end time must be later than the start time.";
  if (!clean(values.venueName || values.location)) errors.venueName = "Enter the venue name.";
  if (!clean(values.address || values.addressLine1)) errors.address = "Enter the event address.";
  if (!clean(values.description) || clean(values.description).length > 4000) {
    errors.description = "Enter an event description of up to 4,000 characters.";
  }
  if (!stringList(values.partnerOrganisations || values.partners).length) {
    errors.partnerOrganisations = "Add at least one partner organisation.";
  }
  if (values.registrationEnabled && !safeHttpUrl(values.registrationUrl)) {
    errors.registrationUrl = "Enter a complete https:// registration URL before enabling registration.";
  }
  [
    ["registrationCount", "Registration total"],
    ["attendanceCount", "Attendance total"],
  ].forEach(([field, label]) => {
    if (values[field] !== null && values[field] !== undefined && clean(values[field]) !== ""
      && optionalNonNegativeInteger(values[field]) === null) {
      errors[field] = `${label} must be a whole number of 0 or more.`;
    }
  });
  const flyerValidation = validateEventFlyer(values.flyerFile || null);
  if (!flyerValidation.valid) errors.flyerFile = flyerValidation.error;
  if (values.flyerFile && clean(values.status) !== "approved") {
    errors.flyerFile = "Choose Approved status before uploading a public event flyer.";
  }
  return errors;
}

export function buildHousingEventRecord(values = {}, existing = {}) {
  const errors = validateHousingEvent(values);
  if (Object.keys(errors).length) {
    const error = new Error("HOUSING_EVENT_INVALID");
    error.validationErrors = errors;
    throw error;
  }
  const title = clean(values.title || values.eventName);
  const date = dateValue(values.date || values.startDate);
  const startTime = timeValue(values.startTime);
  const eventSlug = slugify(
    values.eventSlug
      || values.slug
      || existing.eventSlug
      || existing.slug
      || `${title}-${date}`,
  );
  const eventId = clean(values.eventId || existing.eventId || existing.id || `${HOUSING_DEVELOPMENT_PROGRAMME}-${date}-${slugify(title).slice(0, 48)}`);
  const status = EVENT_STATUSES.has(clean(values.status)) ? clean(values.status) : "draft";
  const surveyIds = stringList(values.surveyIds || values.surveyId);
  const surveySlugs = stringList(values.surveySlugs || values.surveySlug);
  const address = clean(values.address || [values.addressLine1, values.area, values.city, values.postcode]
    .map(clean).filter(Boolean).join(", "));
  const hasExplicitFullAddress = Object.prototype.hasOwnProperty.call(values, "address")
    && clean(values.address);
  const fullAddressChanged = hasExplicitFullAddress
    && clean(values.address) !== clean(existing.address);
  const hasStructuredAddress = ["addressLine1", "area", "city", "postcode"]
    .some((field) => Object.prototype.hasOwnProperty.call(values, field) && clean(values[field]));
  const clearStaleStructuredAddress = fullAddressChanged && !hasStructuredAddress;
  return {
    eventId,
    eventName: title,
    title,
    eventSlug,
    slug: eventSlug,
    eventSeries: HOUSING_DEVELOPMENT_EVENT_SERIES,
    programme: HOUSING_DEVELOPMENT_PROGRAMME,
    eventType: "housing-development",
    type: "event",
    date,
    startDate: date,
    startTime,
    endTime: timeValue(values.endTime),
    timezone: "Europe/London",
    venueName: clean(values.venueName || values.location),
    location: clean(values.venueName || values.location),
    address,
    addressLine1: clearStaleStructuredAddress
      ? address
      : clean(values.addressLine1 || existing.addressLine1 || values.address),
    area: clearStaleStructuredAddress ? "" : clean(values.area || existing.area),
    city: clearStaleStructuredAddress ? "" : clean(values.city || existing.city),
    postcode: clearStaleStructuredAddress ? "" : clean(values.postcode || existing.postcode).toUpperCase(),
    description: clean(values.description),
    partnerOrganisations: stringList(values.partnerOrganisations || values.partners),
    flyerUrl: clean(values.flyerUrl || existing.flyerUrl),
    flyerFileName: clean(values.flyerFileName || existing.flyerFileName),
    flyerContentType: clean(values.flyerContentType || existing.flyerContentType),
    flyerSizeBytes: Number(values.flyerSizeBytes || existing.flyerSizeBytes || 0),
    flyerStoragePath: clean(
      values.flyerStoragePath
        || values.posterStoragePath
        || existing.flyerStoragePath
        || existing.posterStoragePath,
    ),
    registrationEnabled: values.registrationEnabled === true,
    registrationOpen: values.registrationEnabled === true && status === "approved",
    registrationUrl: values.registrationEnabled === true ? safeHttpUrl(values.registrationUrl) : "",
    registrationCount: optionalNonNegativeInteger(
      values.registrationCount ?? existing.registrationCount ?? existing.registrations,
    ),
    attendanceCount: optionalNonNegativeInteger(
      values.attendanceCount ?? existing.attendanceCount ?? existing.attended,
    ),
    surveyId: surveyIds[0] || "",
    surveyIds,
    surveySlug: surveySlugs[0] || "",
    surveySlugs,
    status,
    lifecycleStatus: status === "approved"
      ? date < londonDateKey() ? "past" : "upcoming"
      : status,
    eventDate: Timestamp.fromDate(londonEventStartDate(date, startTime)),
  };
}

function normaliseSnapshot(item) {
  return { id: item.id, ...item.data() };
}

export function subscribeHousingDevelopmentEvents(onData, onError) {
  return onSnapshot(collection(db, "events"), (snapshot) => {
    onData(snapshot.docs.map(normaliseSnapshot).filter((event) => (
      event.programme === HOUSING_DEVELOPMENT_PROGRAMME
        || event.eventSeries === HOUSING_DEVELOPMENT_EVENT_SERIES
        || event.eventType === "housing-development"
    )));
  }, onError);
}

export function subscribeHousingDevelopmentSurveys(onData, onError) {
  return onSnapshot(collection(db, "surveys"), (snapshot) => {
    onData(snapshot.docs.map(normaliseSnapshot).filter((survey) => (
      survey.programme === HOUSING_DEVELOPMENT_PROGRAMME
    )));
  }, onError);
}

export function subscribeHousingDevelopmentResponses(onData, onError) {
  const responseQuery = query(
    collection(db, "surveyResponses"),
    where("programme", "==", HOUSING_DEVELOPMENT_PROGRAMME),
  );
  return onSnapshot(responseQuery, (snapshot) => {
    onData(snapshot.docs.map(normaliseSnapshot));
  }, onError);
}

function surveyAudit(batch, surveyId, action, uid, details = {}) {
  const auditRef = doc(collection(db, "surveyAuditLog"));
  batch.set(auditRef, {
    surveyId,
    programme: HOUSING_DEVELOPMENT_PROGRAMME,
    action,
    details,
    createdAt: serverTimestamp(),
    createdBy: uid,
  });
}

function publicSurveyActorMetadataCleanup() {
  return {
    createdBy: deleteField(),
    updatedBy: deleteField(),
  };
}

function surveyRecordId(survey = {}) {
  return clean(survey.surveyId || survey.id);
}

function surveyEventIds(survey = {}) {
  return stringList(survey.eventIds?.length ? survey.eventIds : survey.eventId);
}

export function reconcileSurveyEventRelationship(survey = {}, eventId, selected) {
  const canonicalEventId = clean(eventId);
  let eventIds = surveyEventIds(survey);
  if (selected && canonicalEventId && !eventIds.includes(canonicalEventId)) {
    eventIds.push(canonicalEventId);
  }
  if (!selected && canonicalEventId) {
    eventIds = eventIds.filter((value) => value !== canonicalEventId);
  }
  const currentPrimary = clean(survey.eventId);
  return {
    eventId: currentPrimary && eventIds.includes(currentPrimary)
      ? currentPrimary
      : eventIds[0] || "",
    eventIds,
  };
}

async function writeHousingEventWithSurveyLinks({
  eventRecord,
  uid,
  create,
  knownSurveys = [],
}) {
  const eventId = eventRecord.eventId;
  const eventReference = doc(db, "events", eventId);
  const selectedIds = stringList(eventRecord.surveyIds || eventRecord.surveyId);
  let savedRecord = eventRecord;
  await runTransaction(db, async (transaction) => {
    const eventSnapshot = await transaction.get(eventReference);
    if (create && eventSnapshot.exists()) throw new Error("An event with this ID already exists.");
    if (!create && !eventSnapshot.exists()) throw new Error("The event no longer exists.");
    const storedEvent = eventSnapshot.exists() ? eventSnapshot.data() : {};
    const candidateIds = [...new Set([
      ...selectedIds,
      ...stringList(storedEvent.surveyIds || storedEvent.surveyId),
      ...knownSurveys
        .filter((survey) => surveyEventIds(survey).includes(eventId))
        .map(surveyRecordId),
    ].filter(Boolean))];
    const surveySnapshots = await Promise.all(candidateIds.map(async (surveyId) => {
      const surveyReference = doc(db, "surveys", surveyId);
      return {
        surveyId,
        surveyReference,
        snapshot: await transaction.get(surveyReference),
      };
    }));
    const missingSelected = surveySnapshots
      .filter(({ surveyId, snapshot }) => selectedIds.includes(surveyId) && !snapshot.exists())
      .map(({ surveyId }) => surveyId);
    if (missingSelected.length) {
      throw new Error("One or more selected surveys no longer exist. Refresh the page and try again.");
    }
    const selectedSurveys = selectedIds.map((surveyId) => surveySnapshots
      .find((item) => item.surveyId === surveyId))
      .filter((item) => item?.snapshot.exists());
    const surveySlugs = selectedSurveys.map(({ surveyId, snapshot }) => (
      clean(snapshot.data().surveySlug || snapshot.data().slug) || surveyId
    ));
    savedRecord = {
      ...eventRecord,
      surveyId: selectedIds[0] || "",
      surveyIds: selectedIds,
      surveySlug: surveySlugs[0] || "",
      surveySlugs,
    };
    const timestamp = serverTimestamp();
    const eventValues = {
      ...savedRecord,
      updatedAt: timestamp,
      updatedBy: uid,
      ...(create ? { createdAt: timestamp, createdBy: uid } : {}),
    };
    if (create) transaction.set(eventReference, eventValues);
    else transaction.update(eventReference, eventValues);
    surveySnapshots.forEach(({ surveyId, surveyReference, snapshot }) => {
      if (!snapshot.exists()) return;
      const survey = snapshot.data();
      const selected = selectedIds.includes(surveyId);
      const relationship = reconcileSurveyEventRelationship(survey, eventId, selected);
      const currentIds = surveyEventIds(survey);
      if (
        relationship.eventId === clean(survey.eventId)
        && relationship.eventIds.length === currentIds.length
        && relationship.eventIds.every((value, index) => value === currentIds[index])
      ) return;
      transaction.update(surveyReference, {
        ...relationship,
        updatedAt: timestamp,
        ...publicSurveyActorMetadataCleanup(),
      });
      surveyAudit(transaction, surveyId, "survey_event_relationship_updated", uid, {
        eventId,
        linked: selected,
      });
    });
  });
  return savedRecord;
}

export async function createHousingDevelopmentEvent(values, flyerFile = null, knownSurveys = []) {
  const uid = requireAdminUid();
  const record = buildHousingEventRecord({ ...values, flyerFile });
  let savedRecord = record;
  const created = await submitEventWithOptionalFlyer({
    file: flyerFile,
    eventId: record.eventId,
    eventRecord: record,
    nonce: window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2),
    saveEvent: async (eventRecord) => {
      savedRecord = await writeHousingEventWithSurveyLinks({
        eventRecord,
        uid,
        create: true,
        knownSurveys,
      });
    },
    uploadFlyer: async (selectedFile, flyerMetadata) => {
      await uploadBytes(ref(storage, flyerMetadata.flyerStoragePath), selectedFile, {
        contentType: flyerMetadata.flyerContentType,
      });
    },
  });
  return { ...created, ...savedRecord, id: record.eventId };
}

export async function replaceHousingDevelopmentEventFlyer(event, file) {
  const uid = requireAdminUid();
  const eventId = clean(event?.eventId || event?.id);
  if (!eventId) throw new Error("A stored event is required before replacing its flyer.");
  if (clean(event.status) !== "approved") {
    throw new Error("Approve the event before uploading its public flyer.");
  }
  const validation = validateEventFlyer(file);
  if (!file || !validation.valid) throw new Error(validation.error || "Choose a flyer to upload.");
  const oldFlyer = {
    flyerUrl: clean(event.flyerUrl),
    flyerFileName: clean(event.flyerFileName),
    flyerContentType: clean(event.flyerContentType),
    flyerSizeBytes: Number(event.flyerSizeBytes || 0),
    flyerStoragePath: clean(event.flyerStoragePath),
  };
  const flyerStoragePath = createEventFlyerStoragePath(
    eventId,
    file.name,
    Date.now(),
    window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2),
  );
  const flyer = {
    flyerUrl: "",
    flyerFileName: clean(file.name),
    flyerContentType: clean(file.type).toLowerCase(),
    flyerSizeBytes: file.size,
    flyerStoragePath,
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  };
  await writeBatchWithSingleUpdate("events", eventId, flyer);
  try {
    await uploadBytes(ref(storage, flyerStoragePath), file, { contentType: flyer.flyerContentType });
  } catch (cause) {
    await writeBatchWithSingleUpdate("events", eventId, {
      ...oldFlyer,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    });
    const error = new Error("The flyer could not be uploaded; the existing flyer was kept.");
    error.cause = cause;
    throw error;
  }
  if (oldFlyer.flyerStoragePath && oldFlyer.flyerStoragePath !== flyerStoragePath) {
    try {
      await deleteObject(ref(storage, oldFlyer.flyerStoragePath));
    } catch {
      // The replacement is already live; failure to remove an unreferenced old object is non-blocking.
    }
  }
  return { ...event, ...flyer, id: eventId, eventId };
}

export async function updateHousingDevelopmentEvent(eventId, values, existing = {}, knownSurveys = []) {
  const uid = requireAdminUid();
  const record = buildHousingEventRecord({ ...values, eventId }, existing);
  return writeHousingEventWithSurveyLinks({
    eventRecord: record,
    uid,
    create: false,
    knownSurveys,
  });
}

async function writeBatchWithSingleUpdate(collectionName, id, values) {
  const batch = writeBatch(db);
  batch.update(doc(db, collectionName, id), values);
  await batch.commit();
}

function eventSurveyLinkFields(
  event = {},
  { removeSurveyId = "", removeSurveySlug = "", addSurveyId = "", addSurveySlug = "" } = {},
) {
  let surveyIds = stringList(event.surveyIds?.length ? event.surveyIds : event.surveyId);
  let surveySlugs = stringList(event.surveySlugs?.length ? event.surveySlugs : event.surveySlug);
  if (removeSurveyId) surveyIds = surveyIds.filter((value) => value !== removeSurveyId);
  if (removeSurveySlug) {
    surveySlugs = surveySlugs.flatMap((value) => (
      value === removeSurveySlug
        ? (!removeSurveyId && addSurveySlug ? [addSurveySlug] : [])
        : [value]
    ));
    surveySlugs = [...new Set(surveySlugs)];
  }
  if (addSurveyId && !surveyIds.includes(addSurveyId)) surveyIds.push(addSurveyId);
  if (addSurveySlug && !surveySlugs.includes(addSurveySlug)) surveySlugs.push(addSurveySlug);
  return {
    surveyId: surveyIds[0] || "",
    surveyIds,
    surveySlug: surveySlugs[0] || "",
    surveySlugs,
  };
}

export async function createHousingDevelopmentSurvey(values) {
  const uid = requireAdminUid();
  const record = buildHousingSurveyRecord(values);
  const surveyId = record.surveyId || doc(collection(db, "surveys")).id;
  const reference = doc(db, "surveys", surveyId);
  const reservationReference = surveySlugReservationReference(record.surveySlug);
  await assertHousingSurveySlugUnique(record.surveySlug, surveyId);
  await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(reference);
    const reservationSnapshot = await transaction.get(reservationReference);
    const eventReference = record.eventId ? doc(db, "events", record.eventId) : null;
    const eventSnapshot = eventReference ? await transaction.get(eventReference) : null;
    if (existing.exists()) throw new Error("A survey with this ID already exists.");
    assertSurveySlugReservationAvailable(reservationSnapshot, surveyId);
    const timestamp = serverTimestamp();
    transaction.set(
      reservationReference,
      surveySlugReservationRecord(
        record.surveySlug,
        surveyId,
        uid,
        timestamp,
        !reservationSnapshot.exists(),
      ),
      { merge: true },
    );
    transaction.set(reference, {
      ...record,
      surveyId,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    const auditRef = doc(collection(db, "surveyAuditLog"));
    transaction.set(auditRef, {
      surveyId,
      programme: HOUSING_DEVELOPMENT_PROGRAMME,
      action: "survey_created",
      details: { status: record.status },
      createdAt: timestamp,
      createdBy: uid,
    });
    if (eventReference && eventSnapshot?.exists()) {
      transaction.update(eventReference, {
        ...eventSurveyLinkFields(eventSnapshot.data(), {
          addSurveyId: surveyId,
          addSurveySlug: record.surveySlug,
        }),
        updatedAt: timestamp,
        updatedBy: uid,
      });
    }
  });
  return { ...record, surveyId, id: surveyId };
}

export async function updateHousingDevelopmentSurvey(surveyId, values, existing = {}) {
  const uid = requireAdminUid();
  const requestedEventIds = Object.prototype.hasOwnProperty.call(values, "eventIds")
    ? stringList(values.eventIds)
    : Object.prototype.hasOwnProperty.call(values, "eventId")
      ? stringList(values.eventId)
      : surveyEventIds(existing);
  const record = buildHousingSurveyRecord({
    ...existing,
    ...values,
    eventId: requestedEventIds[0] || "",
    eventIds: requestedEventIds,
    surveyId,
  }, existing);
  await assertHousingSurveySlugUnique(record.surveySlug, surveyId);
  const reference = doc(db, "surveys", surveyId);
  let savedRecord = record;
  await runTransaction(db, async (transaction) => {
    const storedSnapshot = await transaction.get(reference);
    if (!storedSnapshot.exists()) throw new Error("The survey no longer exists.");
    const stored = { ...existing, ...storedSnapshot.data() };
    assertSurveySlugCanChange(stored, record.surveySlug);
    const oldSurveySlug = clean(stored.surveySlug || stored.slug);
    const reservationReference = surveySlugReservationReference(record.surveySlug);
    const oldReservationReference = oldSurveySlug
      ? surveySlugReservationReference(oldSurveySlug)
      : null;
    const reservationSnapshot = await transaction.get(reservationReference);
    const oldReservationSnapshot = oldReservationReference
      && oldReservationReference.id !== reservationReference.id
      ? await transaction.get(oldReservationReference)
      : reservationSnapshot;
    assertSurveySlugReservationAvailable(reservationSnapshot, surveyId);
    const oldEventIds = surveyEventIds(stored);
    const newEventIds = surveyEventIds(record);
    const eventIds = [...new Set([...oldEventIds, ...newEventIds].filter(Boolean))];
    const eventSnapshots = await Promise.all(eventIds.map(async (eventId) => {
      const eventReference = doc(db, "events", eventId);
      return { eventId, eventReference, snapshot: await transaction.get(eventReference) };
    }));
    const currentVersion = Math.max(1, Number(
      stored.schemaVersion || stored.surveyVersion || stored.version || record.schemaVersion || 1,
    ));
    const nextVersion = currentVersion + 1;
    const timestamp = serverTimestamp();
    savedRecord = {
      ...record,
      schemaVersion: nextVersion,
      surveyVersion: nextVersion,
      version: nextVersion,
    };
    transaction.set(
      reservationReference,
      surveySlugReservationRecord(
        record.surveySlug,
        surveyId,
        uid,
        timestamp,
        !reservationSnapshot.exists(),
      ),
      { merge: true },
    );
    if (
      oldReservationReference
      && oldReservationReference.id !== reservationReference.id
      && oldReservationSnapshot?.exists()
      && clean(oldReservationSnapshot.data().surveyId) === surveyId
    ) {
      transaction.delete(oldReservationReference);
    }
    transaction.update(reference, {
      ...savedRecord,
      updatedAt: timestamp,
      ...publicSurveyActorMetadataCleanup(),
    });
    eventSnapshots.forEach(({ eventId, eventReference, snapshot }) => {
      if (!snapshot.exists()) return;
      const wasLinked = oldEventIds.includes(eventId);
      const isLinked = newEventIds.includes(eventId);
      transaction.update(eventReference, {
        ...eventSurveyLinkFields(snapshot.data(), {
          removeSurveyId: wasLinked && !isLinked ? surveyId : "",
          removeSurveySlug: wasLinked ? oldSurveySlug : "",
          addSurveyId: isLinked ? surveyId : "",
          addSurveySlug: isLinked ? record.surveySlug : "",
        }),
        updatedAt: timestamp,
        updatedBy: uid,
      });
    });
    surveyAudit(transaction, surveyId, "survey_updated", uid, { schemaVersion: nextVersion });
  });
  return { ...savedRecord, id: surveyId };
}

export async function duplicateHousingDevelopmentSurvey(survey) {
  const uid = requireAdminUid();
  const reference = doc(collection(db, "surveys"));
  const baseSlug = slugify(survey.surveySlug || survey.slug || survey.surveyName || "survey-copy");
  const surveySlug = `${baseSlug}-copy-${reference.id.slice(0, 6).toLowerCase()}`;
  const record = buildHousingSurveyRecord({
    ...survey,
    surveyId: reference.id,
    surveyName: `${clean(survey.surveyName || survey.title)} (copy)`,
    surveySlug,
    status: "draft",
    published: false,
    active: false,
    schemaVersion: 1,
    surveyVersion: 1,
    version: 1,
  });
  const reservationReference = surveySlugReservationReference(record.surveySlug);
  await assertHousingSurveySlugUnique(record.surveySlug, reference.id);
  await runTransaction(db, async (transaction) => {
    const reservationSnapshot = await transaction.get(reservationReference);
    const eventReference = record.eventId ? doc(db, "events", record.eventId) : null;
    const eventSnapshot = eventReference ? await transaction.get(eventReference) : null;
    assertSurveySlugReservationAvailable(reservationSnapshot, reference.id);
    const timestamp = serverTimestamp();
    transaction.set(
      reservationReference,
      surveySlugReservationRecord(
        record.surveySlug,
        reference.id,
        uid,
        timestamp,
        !reservationSnapshot.exists(),
      ),
      { merge: true },
    );
    transaction.set(reference, {
      ...record,
      surveyId: reference.id,
      duplicatedFrom: clean(survey.surveyId || survey.id),
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    if (eventReference && eventSnapshot?.exists()) {
      transaction.update(eventReference, {
        ...eventSurveyLinkFields(eventSnapshot.data(), {
          addSurveyId: reference.id,
          addSurveySlug: record.surveySlug,
        }),
        updatedAt: timestamp,
        updatedBy: uid,
      });
    }
    surveyAudit(transaction, reference.id, "survey_duplicated", uid, {
      duplicatedFrom: clean(survey.surveyId || survey.id),
    });
  });
  return { ...record, id: reference.id, surveyId: reference.id };
}

export async function setHousingSurveyStatus(surveyId, status) {
  const uid = requireAdminUid();
  if (!SURVEY_STATUSES.has(status)) throw new Error("Unsupported survey status.");
  const batch = writeBatch(db);
  const timestamp = serverTimestamp();
  const changes = {
    status,
    published: status === "published",
    active: status === "published",
    updatedAt: timestamp,
    ...publicSurveyActorMetadataCleanup(),
  };
  if (status === "published") changes.publishedAt = timestamp;
  if (status === "closed") changes.closedAt = timestamp;
  batch.update(doc(db, "surveys", surveyId), changes);
  surveyAudit(batch, surveyId, `survey_${status}`, uid, {});
  await batch.commit();
}

